<main id="main-content" class="h-screen flex flex-col pl-10 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    Crisis Management
                </h1>
                <div data-popover-target="popover-right" data-popover-placement="right" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-right" role="tooltip"
                    class="absolute z-30 invisible inline-block w-fit max-w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            Provide an introductory statement that includes philosophy, expectations, and directives to
                            school staff, students, and parents. Click 'Confirm' to confirm the statement if no changes
                            are needed.
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <eop-assistant
                *ngIf="selectedCrisisSection !== 'Record of Distribution' && currentPageRoute !== 'training-exercises'"
                [sectionId]="eopSectionTypeId" [subSectionId]="eopSubSectionTypeId"></eop-assistant>
        </div>
    </div>

    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow gap-8 items-start overflow-y-auto">
        <div class="w-1/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="flex flex-row w-full items-center justify-between">
                <h2 class="oswald-font text-3xl font-medium">SECTION</h2>
            </div>
            <div class="h-full w-full flex flex-wrap text-gray-900 overflow-y-auto">
                <div class="h-full w-full mr-2 flex flex-col gap-2">
                    <div class="w-full flex flex-row items-stretch gap-2" *ngFor="let section of crisisSections">
                        <button
                            class="min-h-10 flex-1 px-2.5 py-1.5 flex flex-row gap-2 items-center justify-between rounded-md hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 hover:text-white hover:border-nside-orange-100 group"
                            [ngClass]="{
                                'bg-gray-50 border border-nside-gray-300': selectedCrisisSection !== section,
                                'bg-gradient-to-b from-nside-orange-100 to-nside-orange-200 text-white border-nside-orange-100': selectedCrisisSection === section
                                }" (click)="$event.preventDefault(); selectCrisisSection(section);">
                            <span class="text-start">{{ section }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <form #crisisForm="ngForm" (ngSubmit)="onSubmit()"
            class="w-2/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col flex-grow gap-4 overflow-y-auto">
            <h2 class="oswald-font text-3xl text-nside-orange-400">{{ this.selectedCrisisSection }}</h2>
            <div class="w-full h-full overflow-y-auto flex flex-col flex-grow gap-4 overflow-y-auto">
                <div *ngIf="crisisManagement && sectionFields[selectedCrisisSection] as fields"
                    class="flex flex-col px-1 gap-4">
                    <div *ngFor="let field of fields" class="flex flex-col gap-1">
                        <span>{{ field.label }}</span>

                        <!-- If field is a dropdown field -->
                        <ng-container *ngIf="dropdownFields.includes(field.key); else textInput">
                            <select [name]="field.key" [(ngModel)]="crisisManagement[field.key]" [disabled]="!canSave()"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded block w-full p-2">
                                <option *ngFor="let contact of contactsList" [value]="contact.id">
                                    {{ contact.name }} - {{ contact.email }}
                                </option>
                            </select>
                        </ng-container>

                        <!-- Otherwise, use a text input -->
                        <ng-template #textInput>
                            <input type="text" [name]="field.key" [(ngModel)]="crisisManagement[field.key]"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded block w-full p-2"
                                placeholder="Enter here" [disabled]="!canSave()">
                        </ng-template>
                    </div>
                </div>
            </div>
            <div class="w-full flex flex-row gap-2 items-center justify-end">
                <button *ngIf="canSave()"
                    class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
                    [ngClass]="{
                        'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !isSectionValid(),
                        'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': isSectionValid()
                    }" [disabled]="!isSectionValid()" (click)="$event.preventDefault(); onSubmit()">
                    Save
                </button>
            </div>
        </form>
    </div>
    <!-- APPROVE DOCUMENT BUTTON -->
    <div class="p-2 w-full">
        <div class="w-full bg-white rounded-md shadow-custom-2 p-6 flex gap-4 overflow-y-auto">
            <div class="w-full p-2 border-l-4 bg-gray-50 rounded-r-md flex items-end gap-2" [ngClass]="{
                    'border-green-600': crisisManagement.user_name_approved,
                    'border-nside-gray-300': !crisisManagement.user_name_approved
                }">
                <div class="w-full flex flex-col items-start justify-between">
                    <p class="oswald-font font-normal">
                        Confirmed By: <span class="raleway-font">{{ crisisManagement.user_name_approved || 'Not
                            Confirmed Yet' }}</span>
                    </p>
                    <p class="oswald-font font-normal">
                        Date Confirm: <span class="raleway-font">{{ crisisManagement.date_approved | date: 'MM/dd/yyyy -
                            HH:mm' }}</span>
                    </p>
                    <p *ngIf="canSave()" class="w-full text-end">
                        By clicking this button, I certify that this information is correct and does not need to be
                        updated.
                    </p>
                </div>
                <button *ngIf="canSave()"
                    class="w-20 flex items-center justify-center text-white oswald-font px-4 py-1.5 bg-gradient-to-b rounded-md"
                    [ngClass]="{
                        'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !crisisManagement,
                        'border-green-600 from-green-500 to-green-600 hover:from-green-500 hover:to-green-700': crisisManagement
                    }" [disabled]="!crisisManagement" (click)="$event.preventDefault(); handleApproval();">
                    Confirm
                </button>
            </div>
        </div>
    </div>
    <prv-next-buttons></prv-next-buttons>
</main>

<introduction></introduction>

<popup-message [type]="messageModal" [message]="popupMessage" (close)="resetMessageModal()"></popup-message>

<div *ngIf="loading">
    <spinner></spinner>
</div>